import { ApiFilter, ApiPage, ApiSort } from "@result/rui/utils/api";

import GenericOfflineStore from "#/data-layer/GenericOfflineStore";

const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Generic Offline Store
 */
export default class GenericCacheStore extends GenericOfflineStore {
  /**
   * @var {number} ttl - Time to live for cached items in milliseconds. After this time, cached items are considered stale and will be refreshed from the service on the next query. Default is 5 minutes (300000 ms).
   */
  ttl;

  /**
   * Are we online?
   *
   * This can be determined by a custom function provided in the constructor params,
   * or by using the useOnlineStatus hook if available, or by default it will
   * always return true (considering the app always online).
   *
   * @returns boolean
   */
  // eslint-disable-next-line class-methods-use-this
  isOnline = () => true;

  /**
   * Constructor for the CacheStore class.
   *
   * @param {object} params
   * @param {PokkService} params.service - Service to interact with the API
   * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
   * @param {string} params.ident - Store identifier, name
   * @param {object} params.dataLayer - Optional full data layer instance or configuration object to use instead of the default GenericPokkService instance. If an object is provided, it will be used to construct a new GenericPokkService instance.
   * @param {function} params.mapper - Optional function to map items before storing them in IndexedDB. It should take an item and return the mapped item. This can be useful if you want to store only a subset of the data, or if you want to transform the data in some way before storing it.
   * @param {function} params.filter - Optional function to apply additional client-side filtering to the results from the service. It should take an item and return true if it should be included in the results, or false if it should be excluded.
   */
  constructor(params) {
    super(params);
    this.ttl = params.ttl ?? DEFAULT_CACHE_TTL;
  }

  /**
   * Queries items with given filter/sort/page, and returns them as a list.
   *
   * It will use the service's query method,
   * but can also apply additional client-side filtering/sorting/pagination if needed.
   *
   * @param {object} param
   * @param {function|ApiFilter} param.filter
   * @param {ApiFilter} param.apiFilter - Optional expilicit API filter (if different from filter)
   * @param {function|ApiSort} param.sort
   * @param {ApiSort} param.apiSort - Optional expilicit API sort (if different from sort)
   * @param {number|ApiPage} param.page
   * @param {number} param.perPage - Optional number of items per page
   * @param {boolean} param.fresh - Optional param, bypass cache and read directly from POKK service
   * @returns
   */
  async query({ fresh = false, ...$params } = {}) {
    if (!fresh) {
      // read from cache
      // rawQuery is inherited from GenericOfflineStore,
      // and it will apply the client-side filtering/sorting/pagination as well
      const result = await this.rawQuery($params);

      // figure out if the results are stale or not,
      // based on some timestamp or versioning mechanism
      const now = Date.now();
      const stale = () =>
        result.content.some(
          ({ $timestamp }) => now - $timestamp > (this.ttl ?? DEFAULT_CACHE_TTL)
        );

      if (result.totalElements > 0 && !stale()) {
        // when we are offline and we have valid cached data, return it
        return result;
      }
    }

    // fetch live data from the service,
    const result = await this.dataLayer.query($params);

    // store to cache

    await this.rawInsert(
      ...result.content.map((item) => ({
        ...item,
        $timestamp: Date.now(),
      }))
    );

    return result;
  }

  /**
   *
   * @param {string} id
   * @param {object} params
   * @param {boolean} param.bypass - Optional param, bypass cache and read directly from POKK service
   */
  async read(id, { fresh = false } = {}) {
    if (!fresh) {
      const item = await this.idb.read(this.ident, id);

      const stale = () =>
        item.$timestamp && Date.now() - item.$timestamp > this.ttl;

      if (item && !stale()) {
        return item;
      }
    }

    // fetch live data from the service,
    const item = await this.dataLayer.read(id);

    // store to cache
    await this.rawInsert(item);

    return item;
  }

  // eslint-disable-next-line class-methods-use-this
  async create() {
    throw new Error("Refused to create when using cache store");
  }

  // eslint-disable-next-line class-methods-use-this
  async update() {
    throw new Error("Refused to update when using cache store");
  }

  // eslint-disable-next-line class-methods-use-this
  async delete() {
    throw new Error("Refused to delete when using cache store");
  }

  // eslint-disable-next-line class-methods-use-this
  async upload() {
    throw new Error("Refused to upload when using cache store");
  }

  // eslint-disable-next-line class-methods-use-this
  async sync() {
    throw new Error("Refused to sync when using cache store");
  }
}
